import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  MapPin, ChevronDown, ChevronRight, RefreshCw, AlertCircle, TrendingUp,
  Lightbulb, ArrowRight,
} from "lucide-react";
import {
  BarChart, Bar, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { activeCurrencySymbol } from "@/utils/currency";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const fmt = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
const fmtK = (n) => {
  if (n == null) return "—";
  const v = Number(n) || 0;
  const s = activeCurrencySymbol();
  if (Math.abs(v) >= 10_000_000) return `${s}${(v / 10_000_000).toFixed(1)}Cr`;
  if (Math.abs(v) >= 100_000) return `${s}${(v / 100_000).toFixed(1)}L`;
  if (Math.abs(v) >= 1_000) return `${s}${(v / 1_000).toFixed(1)}K`;
  return `${s}${fmt(v)}`;
};

const TREND_DAYS = [30, 60, 90];
const STAGE_COLORS = ["#93c5fd", "#60a5fa", "#3b82f6", "#10b981", "#059669"];

// ── little stat chip ──
const Chip = ({ label, value, sub, tone = "slate" }) => {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`px-3 py-2 rounded-lg border ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base font-bold leading-tight">{value}</div>
      {sub != null && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};

// ── CRM funnel as horizontal proportional bars ──
function FunnelBars({ funnel }) {
  const top = funnel[0]?.count || 1;
  return (
    <div className="space-y-1" data-testid="ca-funnel">
      {funnel.map((f, i) => {
        const prev = i > 0 ? funnel[i - 1].count : null;
        const drop = prev && prev > 0 ? Math.round((1 - f.count / prev) * 100) : null;
        return (
          <div key={f.stage} className="flex items-center gap-2">
            <span className="w-20 text-[11px] text-slate-500 shrink-0">{f.stage}</span>
            <div className="flex-1 bg-slate-100 rounded h-5 relative overflow-hidden">
              <div
                className="h-full rounded transition-all"
                style={{ width: `${Math.max((f.count / top) * 100, f.count > 0 ? 4 : 0)}%`, backgroundColor: STAGE_COLORS[i] }}
              />
              <span className="absolute inset-y-0 left-2 flex items-center text-[11px] font-semibold text-slate-700">
                {fmt(f.count)}
              </span>
            </div>
            <span className="w-12 text-[10px] text-right shrink-0 text-rose-500">
              {drop != null && drop > 0 ? `-${drop}%` : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── per-city deep panel (cities with Google spend) ──
function CityPanel({ c, trend }) {
  const g = c.google, crm = c.crm, gap = c.gap;
  const roasTone = crm.roas == null ? "slate" : crm.roas < 1 ? "rose" : crm.roas >= 1.5 ? "emerald" : "amber";
  return (
    <div className="border border-slate-200 rounded-xl p-4 mb-4" data-testid={`ca-city-panel-${c.city.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <MapPin className="w-4 h-4 text-blue-600" />
        <h4 className="text-sm font-bold text-slate-800">{c.city}</h4>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
          roasTone === "rose" ? "bg-rose-100 text-rose-700"
            : roasTone === "emerald" ? "bg-emerald-100 text-emerald-700"
            : "bg-amber-100 text-amber-700"}`}>
          ROAS {crm.roas != null ? `${crm.roas}×` : "—"}
        </span>
        <span className="text-[11px] text-slate-400 ml-auto">{fmtK(g.spend)} spend</span>
      </div>

      {/* Google-side funnel chips */}
      <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">Google Ads</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <Chip label="Impressions" value={fmt(g.impressions)} />
        <Chip label="Clicks" value={fmt(g.clicks)} sub={`CTR ${g.ctr ?? "—"}%`} />
        <Chip label="Google conv" value={fmt(g.conversions)} sub={`${fmtK(g.cost_per_conv)}/conv`} tone="blue" />
        <Chip label="Spend" value={fmtK(g.spend)} sub={`CPC ${fmtK(g.cpc)}`} tone="amber" />
        <Chip label="CPL (real)" value={fmtK(crm.cpl)} sub={`${fmt(crm.leads)} CRM leads`} />
      </div>

      {/* The inflation gap */}
      {gap && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-slate-900 text-white text-[12px] flex items-center gap-2 flex-wrap"
             data-testid={`ca-gap-${c.city.toLowerCase()}`}>
          <TrendingUp className="w-4 h-4 text-amber-400" />
          <span>
            Google reports <strong>{fmt(gap.google_conversions)}</strong> conversions · CRM captured{" "}
            <strong className="text-emerald-400">{fmt(gap.crm_paid)}</strong> actually paid
            {gap.inflation_pct != null && <> → <strong className="text-amber-400">{gap.inflation_pct}% inflation</strong></>}.
            Real cost-per-paid <strong>{fmtK(crm.cost_per_paid)}</strong> vs Google’s <strong>{fmtK(g.cost_per_conv)}</strong>/conv.
          </span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* CRM funnel */}
        <div>
          <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">CRM funnel</div>
          <FunnelBars funnel={c.funnel} />
        </div>
        {/* Trend */}
        <div>
          <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">
            Spend vs leads vs paid ({trend?.days || 30}d)
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={trend?.series?.[c.city] || []} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis yAxisId="l" tick={{ fontSize: 9 }} tickFormatter={(v) => fmtK(v)} width={40} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 9 }} width={24} />
              <RTooltip contentStyle={{ fontSize: 11 }} formatter={(v, n) => (n === "spend" ? fmtK(v) : v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line yAxisId="l" type="monotone" dataKey="spend" stroke="#f59e0b" dot={false} strokeWidth={2} />
              <Line yAxisId="r" type="monotone" dataKey="leads" stroke="#3b82f6" dot={false} strokeWidth={2} />
              <Line yAxisId="r" type="monotone" dataKey="paid" stroke="#10b981" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insight */}
      <div className={`mt-3 flex items-start gap-2 px-3 py-2 rounded-lg text-[12px] ${
        crm.roas != null && crm.roas < 1 ? "bg-rose-50 border border-rose-200 text-rose-800"
          : "bg-blue-50 border border-blue-100 text-blue-900"}`}
           data-testid={`ca-insight-${c.city.toLowerCase()}`}>
        {crm.roas != null && crm.roas < 1
          ? <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-rose-500" />
          : <Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />}
        <span>{c.insight}</span>
      </div>
    </div>
  );
}

export default function CityAnalysis({ period }) {
  const [open, setOpen] = useState(false);
  const [trendDays, setTrendDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await axios.get(`${API_URL}/dashboard/services/city-analysis`, {
        params: { period, trend_days: trendDays },
      });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.status === 403 ? "forbidden" : (e.response?.data?.detail || "Failed to load city analysis"));
    } finally {
      setLoading(false);
    }
  }, [period, trendDays]);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const b = data?.blended;
  const gCities = (data?.cities || []).filter((c) => c.has_google);
  const crmOnly = (data?.cities || []).filter((c) => !c.has_google);

  const spendRevData = gCities.map((c) => ({ city: c.city, Spend: c.google.spend, Revenue: c.crm.revenue }));
  const roasData = gCities.map((c) => ({ city: c.city, roas: c.crm.roas || 0 }));
  const gapData = gCities.map((c) => ({
    city: c.city, "Google conv": c.google.conversions, "CRM booked": c.crm.booked, "CRM paid": c.crm.paid,
  }));

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="svc-city-analysis">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="ca-toggle"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <MapPin className="w-4 h-4 text-blue-600" />
        <h3 className="text-sm font-semibold text-slate-700">City Deep-Dive — Google spend vs CRM reality</h3>
        {loading && <RefreshCw className="w-3 h-3 animate-spin text-slate-400" />}
        <span className="text-[11px] text-slate-400 ml-auto">
          {open ? "Click to collapse" : "Where every ad rupee really converts — click to expand"}
        </span>
      </button>

      {open && (
        <div className="mt-4">
          {err === "forbidden" ? (
            <p className="text-sm text-slate-400 py-4">City analysis needs finance access.</p>
          ) : err ? (
            <p className="text-sm text-rose-500 py-4">{err}</p>
          ) : !data ? (
            <p className="text-sm text-slate-400 py-4">Loading…</p>
          ) : (
            <>
              {/* controls */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-[11px] text-slate-400">Period: <strong className="text-slate-600 uppercase">{data.period}</strong> · trend</span>
                <div className="flex gap-1 bg-slate-100 rounded-md p-0.5" data-testid="ca-trend-toggle">
                  {TREND_DAYS.map((d) => (
                    <button key={d} onClick={() => setTrendDays(d)}
                      className={`px-2 py-0.5 text-[11px] font-medium rounded ${trendDays === d ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
                      data-testid={`ca-trend-${d}`}>{d}d</button>
                  ))}
                </div>
                <button onClick={fetchData} className="ml-auto text-slate-400 hover:text-slate-600" data-testid="ca-refresh">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </button>
              </div>

              {/* HERO gap */}
              {b && (
                <div className="mb-3 px-4 py-3 rounded-xl bg-gradient-to-r from-slate-900 to-slate-800 text-white" data-testid="ca-hero">
                  <div className="text-[11px] uppercase tracking-wide text-slate-300 mb-1">Google says vs CRM reality ({data.period})</div>
                  <div className="text-[13px] leading-relaxed">
                    Google claims <strong className="text-amber-400">{fmt(b.google_conversions)}</strong> conversions;
                    CRM captured <strong className="text-emerald-400">{fmt(b.crm_paid)}</strong> actually paid
                    {b.inflation_pct != null && <> — <strong className="text-amber-400">{b.inflation_pct}% inflation</strong></>}.
                    True cost-per-paid <strong>{fmtK(b.cost_per_paid)}</strong> vs Google’s <strong>{fmtK(b.google_cost_per_conv)}</strong>/conv.
                    Blended ROAS <strong className={b.roas >= 1 ? "text-emerald-400" : "text-rose-400"}>{b.roas != null ? `${b.roas}×` : "—"}</strong>
                    {" "}on <strong>{fmtK(b.spend)}</strong> spend → <strong>{fmtK(b.revenue)}</strong> revenue.
                  </div>
                </div>
              )}

              {/* Budget reco */}
              {data.reco && (
                <div className="mb-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-900"
                     data-testid="ca-reco">
                  <ArrowRight className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
                  <span><strong>Budget move:</strong> {data.reco.text}</span>
                </div>
              )}

              {/* Charts */}
              {gCities.length > 0 && (
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="border border-slate-200 rounded-xl p-3" data-testid="ca-chart-spend-rev">
                    <div className="text-[11px] font-medium text-slate-500 mb-2">Ad spend vs CRM revenue by city</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={spendRevData} margin={{ top: 4, right: 4, left: -6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                        <XAxis dataKey="city" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtK(v)} width={44} />
                        <RTooltip formatter={(v) => fmtK(v)} contentStyle={{ fontSize: 11 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Spend" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-3" data-testid="ca-chart-roas">
                    <div className="text-[11px] font-medium text-slate-500 mb-2">ROAS by city (below 1× = losing money)</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={roasData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                        <XAxis dataKey="city" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 10 }} width={30} />
                        <RTooltip formatter={(v) => `${v}×`} contentStyle={{ fontSize: 11 }} />
                        <ReferenceLine y={1} stroke="#64748b" strokeDasharray="4 4" />
                        <Bar dataKey="roas" radius={[4, 4, 0, 0]}>
                          {roasData.map((d, i) => <Cell key={i} fill={d.roas >= 1 ? "#10b981" : "#ef4444"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Google conv vs CRM booked/paid — the inflation */}
              {gapData.length > 0 && (
                <div className="border border-slate-200 rounded-xl p-3 mb-4" data-testid="ca-chart-gap">
                  <div className="text-[11px] font-medium text-slate-500 mb-2">
                    Google-reported conversions vs CRM Booked vs CRM Paid (the inflation gap)
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={gapData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="city" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 10 }} width={36} />
                      <RTooltip contentStyle={{ fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Google conv" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="CRM booked" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="CRM paid" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Per-city deep panels */}
              {gCities.map((c) => <CityPanel key={c.city} c={c} trend={data.trend} />)}

              {/* CRM-only cities */}
              {crmOnly.length > 0 && (
                <div className="mt-2" data-testid="ca-crm-only">
                  <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-2">
                    Other cities (no ad spend — CRM funnel only)
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wide border-b border-slate-100">
                          <th className="text-left py-2 px-2">City</th>
                          <th className="text-right py-2 px-2">Leads</th>
                          <th className="text-right py-2 px-2">Quoted</th>
                          <th className="text-right py-2 px-2">Booked</th>
                          <th className="text-right py-2 px-2">Paid</th>
                          <th className="text-right py-2 px-2">Conv%</th>
                          <th className="text-right py-2 px-2">Revenue</th>
                          <th className="text-left py-2 px-2">Next action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crmOnly.map((c) => (
                          <tr key={c.city} className="border-b border-slate-50 hover:bg-slate-50/50 align-top">
                            <td className="py-2 px-2 font-medium text-slate-800">{c.city}</td>
                            <td className="py-2 px-2 text-right text-slate-700">{fmt(c.crm.leads)}</td>
                            <td className="py-2 px-2 text-right text-slate-600">{fmt(c.crm.quoted)}</td>
                            <td className="py-2 px-2 text-right text-blue-700">{fmt(c.crm.booked)}</td>
                            <td className="py-2 px-2 text-right text-emerald-700 font-medium">{fmt(c.crm.paid)}</td>
                            <td className="py-2 px-2 text-right text-slate-600">{c.crm.conv_pct ?? 0}%</td>
                            <td className="py-2 px-2 text-right font-semibold text-slate-900">{fmtK(c.crm.revenue)}</td>
                            <td className="py-2 px-2 text-[11px] text-slate-500 max-w-xs">{c.insight}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <p className="text-[10px] text-slate-400 mt-3">
                Google conv = Google’s in-tag count (a lead/call). CRM Paid = a customer who actually paid us.
                City spend is from city-tagged Google campaigns; Meta/manual aren’t city-tagged. as of {data.as_of ? new Date(data.as_of).toLocaleString("en-IN") : "—"}.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
